import React, { Component } from "react";
import * as Sentry from "@sentry/react";
import styled from "styled-components/macro";
import ScrollToTop from "../../components/utils/ScrollToTop";
import { getFormattedDate } from "../../utils/textUtils";
import { sanitiseHtml } from "../../utils/sanitiseHtml";
import { TextareaQuestion } from "../../components/TextQuestion";
import FileUploadQuestion from "../../components/FileUploadQuestion";
import Confirmation from "../../components/Confirmation";
import Image from "../../components/Image";
import imageOfficeWorking from "../../images/office-working.png";
import Action from "../../components/Action";
import Button from "../../components/Button";
import Card from "../../components/DashboardCard";
import ClientReply from "./ClientReply";
import { AttachFile } from "@styled-icons/material";
import {
  StyledEnquiryDoablePageHeader,
  StyledEnquiryMessage,
  StyledPlainEnquiryMessage,
  StyledReplyForm,
  StyledReplyPrompt,
  StyledJunoEnquiryActivity,
  StyledJunoEnquiryHeading,
  StyledEnquiryActivity,
  StyledEnquiryHeading,
  StyledAttachmentList,
} from "./EnquiryDoableStyles";

// True if a message is the HTML the staff app sends, false if it's legacy plain text.
// markdown_to_html always opens with a block tag; plain text opens with prose or a marker.
// Temporary, until webapp-alpha #30268 sends HTML for every message: see JUNO-3018.
export const looksLikeHtml = (content) => /^\s*<(p|ul|ol|h[1-6]|table|blockquote|pre|div)[\s>]/i.test(content);

// The enquiry's updates, with the client's last reply added when the staff app hasn't yet put it
// in the conversation
export const enquiryConversation = (doable) => {
  const conversation = doable.configuration.conversation || [];
  if (!doable.submission || !doable.submission.enquiry_update.client_reply) {
    return conversation;
  }

  const lastUpdateFromClient = conversation
    .slice()
    .reverse()
    .find((c) => c.type === "update_from_client");
  if (!!lastUpdateFromClient && new Date(lastUpdateFromClient.created_at) >= new Date(doable.submission.submitted_at)) {
    return conversation;
  }

  return [
    ...conversation,
    {
      type: "update_from_client",
      created_at: doable.submission.submitted_at,
      content: doable.submission.enquiry_update.client_reply,
      attachments: doable.submission.uploaded_documents || [],
    },
  ];
};

class EnquiryDoable extends Component {
  constructor(props) {
    super(props);
    this.state = {
      response: "",
      documents: [],
      errors: {},
      showResponseForm: this.props.doable.status === "available", // If it's available, it needs a reply
      showUpload: false,
      uploading: false,
      submitted: false,
      submitting: false,
      failed: false,
    };
  }

  componentDidMount() {
    if (this.hasInitiated) return;
    this.hasInitiated = true;

    if (this.props.submissionEnabled && this.props.doable.status === "available_to_read") {
      // Mark it as read.
      fetch(`/api/dashboard/doable/${this.props.doable.id}`, {
        method: "PUT",
        credentials: "same-origin",
        body: JSON.stringify({
          enquiry_update: { mark_as_read: true },
        }),
      });
    }
  }

  createContent(content) {
    return { __html: sanitiseHtml(content) };
  }

  handleResponseChange = (r) => {
    this.setState({ response: r });
  };

  handleUploadsChanged = (uploads) => {
    this.setState({ documents: uploads });
  };

  handleUploadInProgress = (inProgress) => {
    this.setState({ uploading: inProgress });
  };

  handleValidation() {
    const response = this.state.response;
    let formIsValid = true;
    let errors = {};

    if (!response) {
      formIsValid = false;
      errors["response"] = true;
    }

    this.setState({ errors });
    return formIsValid;
  }

  submitResponse = (event) => {
    event.preventDefault();

    if (!this.handleValidation()) {
      return;
    }

    this.setState({ submitting: true });

    const responseData = {
      enquiry_update: {
        client_reply: this.state.response,
      },
      uploaded_documents: this.state.documents.map((upload) => ({
        identifier: "enquiry_response",
        upload_id: upload.upload_id,
      })),
    };

    fetch(`/api/dashboard/doable/${this.props.doable.id}`, {
      method: "PUT",
      credentials: "same-origin",
      body: JSON.stringify(responseData),
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error("Non-success response");
        }
        this.setState({
          submitted: true,
          submitting: false,
        });
      })
      .catch((err) => {
        Sentry.captureException(err, {
          tags: {
            action: "EnquiryDoableSubmission",
            caseRef: this.props.caseRef,
            doableId: this.props.doable.id,
          },
        });
        this.setState({
          failed: true,
          submitting: false,
        });
      });
  };

  showResponseForm = () => {
    this.setState({ showResponseForm: true });
  };

  showUpload = () => {
    this.setState({ showUpload: true });
  };

  submitDisabled = () => {
    return !this.props.submissionEnabled || !this.state.response || this.state.uploading || this.state.submitting;
  };

  conversationIncludingSubmission = () => enquiryConversation(this.props.doable);

  render() {
    if (!this.props.doable) return null;

    if (this.state.failed) {
      return (
        <React.Fragment>
          <ScrollToTop />
          <Card title="Sorry, that didn’t work" removeTopPadding headingSize="2">
            <p>Failed to submit response.</p>
            <Action small onClick={this.props.returnToDashboard}>
              Take me back to try again
            </Action>
          </Card>
        </React.Fragment>
      );
    }

    if (this.state.submitted) {
      return (
        <>
          <ScrollToTop />
          <Confirmation title="Thanks! We’ve got your reply">
            <p>As soon as we need anything else we’ll drop you an email with a link to your dashboard.</p>
            <Action small onClick={this.props.returnToDashboard}>
              Take me back to my dashboard
            </Action>
          </Confirmation>
          <Image src={imageOfficeWorking} fullWidth={true} />
        </>
      );
    }

    let confirmationText;
    if (this.props.doable.status === "complete") {
      if (this.props.doable.submission?.enquiry_update.mark_as_read) {
        confirmationText = "We’ve got everything we need right now";
      } else {
        confirmationText = "Thanks for answering our question - we’ve got your reply";
      }
    }

    let addResponseText;
    if (
      this.props.doable.status === "complete" &&
      this.props.doable.submission &&
      !this.props.doable.submission.enquiry_update.mark_as_read
    ) {
      // Was submitted with a response
      addResponseText = "Send another reply to Juno";
    } else {
      // Is/was a notification update, so no response was previously submitted
      addResponseText = "Send a reply to Juno";
    }
    const showAddReplyControl = this.props.submissionEnabled && !this.state.showResponseForm;

    return (
      <React.Fragment>
        <ScrollToTop />
        <StyledEnquiryDoablePageHeader>
          {this.props.doable.title && this.props.doable.title}
        </StyledEnquiryDoablePageHeader>

        {this.props.doable.status === "complete" && <Confirmation title={confirmationText} submission={true} />}

        <StyledJunoEnquiryActivity>
          {/* This is generic text, per transaction type - about the enquiry  */}
          {/* For example, we'd like your help answering the question below */}
          {this.props.doable.description && (
            <StyledJunoEnquiryHeading dangerouslySetInnerHTML={this.createContent(this.props.doable.description)} />
          )}

          {/* This is the original enquiry  */}
          {this.props.doable.configuration.question && (
            <StyledEnquiryMessage
              dangerouslySetInnerHTML={this.createContent(this.props.doable.configuration.question)}
            />
          )}

          {this.props.doable.configuration.attachments && (
            <StyledAttachmentList>
              {this.props.doable.configuration.attachments.map((file, index) => {
                return (
                  <li key={`enquiry_attachment_${index}`}>
                    <AttachFile size="18" />
                    <a href={file.link} target="_blank" rel="noreferrer">
                      {file.display_name}
                    </a>
                  </li>
                );
              })}
            </StyledAttachmentList>
          )}
        </StyledJunoEnquiryActivity>

        {/* Provide all the context of the conversation */}

        {this.conversationIncludingSubmission().map((update, index, conversation) => {
          const isLastItem = !!(conversation.length - 1 === index);

          if (update.type === "update_to_client") {
            return (
              <StyledJunoEnquiryActivity key={`update_to_client_${index}`} data-testid={`update_to_client_${index}`}>
                <StyledJunoEnquiryHeading>
                  Juno {update.needs_reply ? "asked" : "said"}: <span>{getFormattedDate(update.created_at)}</span>
                </StyledJunoEnquiryHeading>
                {looksLikeHtml(update.content) ? (
                  <StyledEnquiryMessage dangerouslySetInnerHTML={this.createContent(update.content)} />
                ) : (
                  <StyledPlainEnquiryMessage>{update.content}</StyledPlainEnquiryMessage>
                )}

                {update.attachments && (
                  <StyledAttachmentList>
                    {update.attachments.map((file, index) => {
                      return (
                        <li key={`update_to_client_file_${index}`}>
                          <AttachFile size="18" />
                          <a href={file.link} target="_blank" rel="noreferrer">
                            {file.display_name}
                          </a>
                        </li>
                      );
                    })}
                  </StyledAttachmentList>
                )}

                {isLastItem && showAddReplyControl && (
                  <StyledReplyPrompt>
                    <StyledAddItem onClick={this.showResponseForm}>
                      <StyledText>{addResponseText}</StyledText>
                    </StyledAddItem>
                  </StyledReplyPrompt>
                )}
              </StyledJunoEnquiryActivity>
            );
          }

          if (update.type === "update_from_client") {
            return (
              <StyledEnquiryActivity key={`update_from_client_${index}`}>
                <StyledEnquiryHeading>
                  You replied:
                  {update.created_at && <span>{getFormattedDate(update.created_at)}</span>}
                </StyledEnquiryHeading>
                <ClientReply content={update.content} />

                {update.attachments && (
                  <StyledAttachmentList>
                    {update.attachments.map((file, index) => {
                      return (
                        <li key={`update_from_client_file_${index}`}>
                          {(file.link && (
                            <>
                              <AttachFile size="18" />
                              <a href={file.link} target="_blank" rel="noreferrer">
                                {file.filename}
                              </a>
                            </>
                          )) || (
                            <>
                              <AttachFile size="18" />
                              {file.filename}
                            </>
                          )}
                        </li>
                      );
                    })}
                  </StyledAttachmentList>
                )}

                {isLastItem && showAddReplyControl && (
                  <StyledReplyPrompt>
                    <StyledAddItem onClick={this.showResponseForm}>
                      <StyledText>{addResponseText}</StyledText>
                    </StyledAddItem>
                  </StyledReplyPrompt>
                )}
              </StyledEnquiryActivity>
            );
          }

          console.warn(
            `EnquiryDoable: Unknown update type "${update.type}" at index ${index}. This update will not be displayed.`,
            update,
          );
          return null;
        })}

        {this.state.showResponseForm && (
          <StyledEnquiryActivity>
            <StyledEnquiryHeading>Your reply</StyledEnquiryHeading>
            <StyledReplyForm>
              <TextareaQuestion
                question="Enter your response below"
                value={this.state.response}
                valueEntered={this.handleResponseChange}
                validationError={this.state.errors.response ? "Please enter a response" : null}
                disabled={!this.props.submissionEnabled}
              />

              {this.props.submissionEnabled && !this.state.showUpload && (
                <StyledAddItem onClick={this.showUpload}>
                  <StyledIcon>
                    <AttachFile size="24" />
                  </StyledIcon>
                  <StyledText>Attach a document</StyledText>
                </StyledAddItem>
              )}

              {this.state.showUpload && (
                <FileUploadQuestion
                  question="Attach documents to your reply"
                  multiple={true}
                  caseRef={this.props.caseRef}
                  doableId={this.props.doable.id}
                  onUploadsChanged={this.handleUploadsChanged}
                  onUploadInProgress={this.handleUploadInProgress}
                />
              )}

              <StyledActions>
                <Button onClick={this.submitResponse} disabled={this.submitDisabled()}>
                  Save your reply
                </Button>
              </StyledActions>
            </StyledReplyForm>
          </StyledEnquiryActivity>
        )}
      </React.Fragment>
    );
  }
}

export default EnquiryDoable;

const StyledActions = styled.div`
  display: flex;
  align-items: center;
  margin: 16px 0 0 0;

  Button {
    margin-right: 0.8em;
    font-size: var(--juno-font-size-body-sm);
  }
`;

export const StyledAddItem = styled.div`
  margin-top: -0.5em;
  color: var(--juno-color-link);
  width: fit-content;
  display: flex;
  align-items: center;

  &:hover {
    border-bottom: 0;
  }
`;

export const StyledIcon = styled.div`
  svg {
    fill: var(--juno-color-link);
  }

  &:hover {
    svg {
      fill: var(--juno-color-link-hover);
    }
  }
`;

export const StyledText = styled.div`
  margin-left: 4px;
  font-size: var(--juno-font-size-body-sm);
  border-bottom: 1px solid transparent;

  &:hover {
    border-bottom-color: var(--juno-color-link-hover);
  }
`;
